import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './ChatWindow.css';
import useMessageStore from '../store/messageStore';
import { DATE_LOCALE } from '../i18n';

function ChatWindow({ onClose, documentId }) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const pollingIntervalRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const prevDocumentIdRef = useRef(documentId);
  
  // Используем хранилище сообщений
  const { 
    getConversations, 
    fetchConversations, 
    sendMessage, 
    isLoading 
  } = useMessageStore();
  
  // Получаем диалоги для текущего документа
  const conversations = getConversations(documentId);
  const loading = isLoading(documentId);

  // Закрываем окно чата при смене документа
  useEffect(() => {
    if (prevDocumentIdRef.current !== documentId) {
      onClose();
    }
    prevDocumentIdRef.current = documentId;
  }, [documentId, onClose]);

  // Устанавливаем фокус на поле ввода при открытии чата
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Прокрутка чата вниз при изменении сообщений
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    
    // Если загружаем старые сообщения, позиция скролла уже обрабатывается в handleScroll
    if (isLoadingMore) return;
    
    // Получаем флаг, нужно ли прокручивать вниз
    const shouldScroll = useMessageStore.getState().shouldScrollToBottom[documentId];
    
    // Проверяем, находится ли пользователь в нижней части чата
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    
    // Скроллим вниз только если:
    // 1. Флаг shouldScroll установлен
    // 2. Пользователь уже находится внизу чата
    // 3. Появилось новое сообщение с ответом
    const hasNewAnswer = conversations.length > 0 && 
                         conversations[conversations.length - 1].answer !== null;
    
    if (shouldScroll || isNearBottom || hasNewAnswer) {
      container.scrollTop = container.scrollHeight;
    }
  }, [conversations, documentId, isLoadingMore]);

  // Загрузка истории диалогов при открытии чата
  useEffect(() => {
    if (documentId) {
      // Загружаем сообщения сразу при открытии
      fetchConversations(documentId);
      
      // Настраиваем поллинг каждые 3 секунды
      pollingIntervalRef.current = setInterval(() => {
        // Запоминаем текущую позицию скролла перед обновлением
        const container = messagesContainerRef.current;
        if (container) {
          const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
          
          // Выполняем запрос на обновление сообщений
          fetchConversations(documentId, null, false, isNearBottom);
        } else {
          fetchConversations(documentId);
        }
      }, 3000);
    }
    
    // Очищаем интервал при закрытии компонента
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [documentId, fetchConversations]);

  // Обработчик скролла для подгрузки старых сообщений
  const handleScroll = useCallback(async () => {
    const container = messagesContainerRef.current;
    
    // Если скролл близок к верху и есть еще сообщения для загрузки
    if (container && container.scrollTop < 50 && !isLoadingMore && hasMoreMessages) {
      // console.log("Loading more messages...");
      setIsLoadingMore(true);
      
      // Запоминаем текущую высоту контента и позицию скролла
      const oldScrollHeight = container.scrollHeight;
      const oldScrollTop = container.scrollTop;
      
      // Получаем ID самого старого сообщения для пагинации
      const oldestMessage = conversations[0];
      if (oldestMessage && oldestMessage.id) {
        // console.log(`Oldest message ID: ${oldestMessage.id}`);
        
        // Загружаем предыдущие сообщения
        const count = await fetchConversations(documentId, oldestMessage.id, true);
        // console.log(`Loaded ${count} more messages`);
        
        // После загрузки восстанавливаем позицию скролла
        setTimeout(() => {
          if (container) {
            // Вычисляем разницу в высоте контента
            const newScrollHeight = container.scrollHeight;
            const scrollHeightDiff = newScrollHeight - oldScrollHeight;
            
            // Устанавливаем новую позицию скролла, сохраняя относительное положение
            container.scrollTop = oldScrollTop + scrollHeightDiff;
          }
        }, 0);
        
        // Если получено меньше 10 сообщений, значит больше нет
        if (count < 10) {
          // console.log("No more messages to load");
          setHasMoreMessages(false);
        }
      } else {
        // console.log("No oldest message ID found, stopping pagination");
        setHasMoreMessages(false);
      }
      
      setIsLoadingMore(false);
    }
  }, [conversations, documentId, fetchConversations, hasMoreMessages, isLoadingMore]);

  // Добавляем обработчик скролла
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleSubmit = async () => {
    if (!inputValue.trim()) return;
    
    await sendMessage(documentId, inputValue);
    setInputValue('');
    
    // Возвращаем фокус на поле ввода после отправки сообщения
    if (inputRef.current) {
      inputRef.current.focus();
    }
    
    // Прокручиваем чат вниз после отправки сообщения
    if (messagesContainerRef.current) {
      setTimeout(() => {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }, 0);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  // Функция для форматирования времени с учетом локального часового пояса
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    // Проверяем формат timestamp
    let date;
    if (typeof timestamp === 'string') {
      // Если timestamp - строка ISO, создаем Date объект
      // Явно указываем, что это UTC, если в строке нет 'Z' или часового пояса
      if (!timestamp.endsWith('Z') && !timestamp.includes('+')) {
        timestamp = timestamp + 'Z';
      }
      date = new Date(timestamp);
    } else {
      // Если timestamp уже объект Date или число
      date = new Date(timestamp);
    }
    
    // Форматируем дату и время в локальном часовом поясе пользователя с учётом языка
    const datePart = date.toLocaleDateString(DATE_LOCALE, {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
    const timePart = date.toLocaleTimeString(DATE_LOCALE, {
      hour: '2-digit',
      minute: '2-digit',
    });

    return `${datePart} // ${timePart}`;
  };

  // Отображаем диалоги
  const renderConversations = () => {
    return (
      <>
        {isLoadingMore && (
          <div className="loading-more">
            <div className="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}
        
        {conversations.map((conv, index) => (
          <div key={`conv-${index}-${conv.id || index}`} className="message-group">
            <div className="chat-message user-message">
              <div className="message-content">{conv.question}</div>
              {conv.questionTimestamp && (
                <div className="message-timestamp">
                  {formatTime(conv.questionTimestamp)}
                </div>
              )}
            </div>
            {conv.answer ? (
              <div className="chat-message ai-message">
                <div className="message-content">{conv.answer}</div>
                {conv.answerTimestamp && (
                  <div className="message-timestamp">
                    {formatTime(conv.answerTimestamp)}
                  </div>
                )}
              </div>
            ) : (
              <div className="chat-message ai-message empty-answer">
                <div className="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
          </div>
        ))}
      </>
    );
  };

  return (
    <div className="chat-window">
      <div className="chat-window__header">
        <h3>{t('chat.title')}</h3>
        <button className="chat-window__close" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
      </div>
      
      <div 
        className="chat-window__messages" 
        ref={messagesContainerRef}
      >
        {renderConversations()}
      </div>
      
      <div className="chat-window__input-container">
        <input 
          ref={inputRef}
          type="text" 
          className="chat-window__input"
          placeholder={t('chat.inputPlaceholder')}
          value={inputValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
        />
        <button 
          className="chat-window__send"
          onClick={handleSubmit}
          disabled={loading}
        >
          <i className="fas fa-paper-plane"></i>
        </button>
      </div>
    </div>
  );
}

export default ChatWindow; 
